<template>
  <v-content>
    <v-container class="py-12">
      <h2 class="display-1" data-testid="review-heading">
        Does this look right?
      </h2>
      <p class="mt-2">
        Please review your submission for accuracy so we can
        improve the way we tag calls on our network.
      </p>

      <div class="mt-12 px-6 py-8" style="border-radius: 0; border: 1px solid #979797" flat>
        <h2 class="headline">What went wrong?</h2>
        <p class="mt-4">
          {{ data.category }}<br v-if="data.reason" />
          {{ data.reason }}
        </p>

        <h2 class="headline">Caller details</h2>
        <div class="mt-4">
          <p>
            <span data-testid="review-phone-number"><b>Phone number:</b> {{ formattedPhoneNumber }} <br/></span>
            <span v-if="data.name"><b>Incoming Caller ID:</b> {{ data.name }} <br/></span>
            <span v-if="data.date"><b>Date of call:</b> {{ data.date }} <br/></span>
            <span v-if="data.time"><b>Time of call:</b> {{ data.time | formatTime }} <br/></span>
          </p>

          <p v-if="data.isPrerecorded" class="mt-4">
            This call was an articial or pre-recorded voicemail.
          </p>
        </div>
      </div>

      <p class="mt-6">
        If the information above is correct, go ahead and submit your report.
      </p>

      <div class="mt-8">
        <vue-recaptcha :sitekey=getRecaptchaSiteKey @verify="onCaptchaVerified" @expired="onCaptchaExpired"></vue-recaptcha>
      </div>

      <div class="mt-12">
        <v-btn
          data-testid="btn-go-back"
          style="width: 100px"
          :color="isMagenta ? 'primary' : 'warning'"
          large
          text
          @click="step = 'Details'"
        >Go Back</v-btn>

        <v-btn
          data-testid="btn-submit"
          :color="isMagenta ? 'primary' : 'warning'"
          class="ml-4"
          type="submit"
          large
          :style="{
            'border-radius': isMagenta ? 0 : undefined
          }"
          elevation="0"
          :loading="loading"
          :disabled="!isCaptchaVerified"
          @click="onSubmit"
        >Submit report</v-btn>
      </div>
    </v-container>
  </v-content>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, getCurrentInstance, PropType } from 'vue'
import axios from 'axios'
import moment from 'moment'
import { VueRecaptcha } from 'vue-recaptcha'

import config from '@/config'
import { CallDetailsInformation } from '@/types'
import { sendReport, getRecaptchaSiteKey } from '@/utils'

function formatPhoneNumber (num: string) {
  const npa = num.slice(0, 3)
  const nxx = num.slice(3, 6)
  const local = num.slice(6, 10)
  return `(${npa}) ${nxx}-${local}`
}

export default defineComponent({
  name: 'ReviewForm',
  components: { VueRecaptcha },
  filters: {
    formatTime (time: string) {
      return time ? moment(time, ['HH:mm']).format('h:mm A') : null
    }
  },
  props: {
    formStep: {
      type: String as PropType<'Details' | 'Review' | 'Confirmation' | 'Failed'>,
      required: true
    },
    callDetails: {
      type: Object as PropType<CallDetailsInformation>,
      required: true
    }
  },
  emits: ['update:formStep', 'update:callDetails'],
  setup (props, { emit }) {
    const instance = getCurrentInstance()
    const loading = ref(false)
    const isCaptchaVerified = ref(false)
    const recaptchaToken = ref('')

    const step = computed({
      get: () => props.formStep,
      set: (value) => emit('update:formStep', value)
    })

    const data = computed({
      get: () => props.callDetails,
      set: (value) => emit('update:callDetails', value)
    })

    const isMagenta = computed(() => config.isMagenta)

    const recaptchaSiteKey = computed(() => getRecaptchaSiteKey())

    const formattedPhoneNumber = computed(() => {
      const phoneNumber = data.value.number

      if (phoneNumber.length === 10) {
        return formatPhoneNumber(phoneNumber)
      } else if (phoneNumber.length === 11 && phoneNumber.startsWith('1')) {
        return '+1 ' + formatPhoneNumber(phoneNumber.slice(-10))
      } else {
        return phoneNumber
      }
    })

    async function onSubmit () {
      if (isCaptchaVerified.value) {
        try {
          loading.value = true
          instance?.proxy?.$gtag.event('Submit', {
            event_category: isMagenta.value ? 'T-Mobile' : 'Metro'
          })
          const response = await sendReport(data.value, recaptchaToken.value)
          instance?.proxy?.$gtag.event('Success Status ' + response.status, {
            event_category: isMagenta.value ? 'T-Mobile' : 'Metro'
          })
          step.value = 'Confirmation'
        } catch (e: unknown) {
          console.error(e)
          const status = axios.isAxiosError(e) ? e.response?.status : undefined
          instance?.proxy?.$gtag.event('Error Status ' + status, {
            event_category: isMagenta.value ? 'T-Mobile' : 'Metro'
          })
          step.value = 'Failed'
        } finally {
          loading.value = false
        }
      }
    }

    function onCaptchaVerified (token: string) {
      recaptchaToken.value = token
      isCaptchaVerified.value = true
    }

    function onCaptchaExpired () {
      isCaptchaVerified.value = false
      recaptchaToken.value = ''
    }

    onMounted(() => {
      // if the number being reported matches the number(s) we use for automated healthchecks, allow bypass of recaptcha
      isCaptchaVerified.value = config.healthCheckNumbers.includes(data.value.number)
    })

    return {
      loading,
      isCaptchaVerified,
      step,
      data,
      isMagenta,
      getRecaptchaSiteKey: recaptchaSiteKey,
      formattedPhoneNumber,
      onSubmit,
      onCaptchaVerified,
      onCaptchaExpired
    }
  }
})
</script>
